import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Map, CustomOverlayMap, useKakaoLoader } from 'react-kakao-maps-sdk';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { ServiceNav } from '@/components/organisms/ServiceNav';
import { SEOHead } from '@/components/atoms/SEOHead';
import { RealEstateTrendChart } from '@/components/charts/RealEstateTrendChart';
import { ComplexMarkerLayer, type ComplexMarker } from '@/components/map/ComplexMarkerLayer';
import { ChevronLeft } from 'lucide-react';

const KAKAO_KEY = import.meta.env.VITE_KAKAO_JS_KEY as string | undefined;

interface RegionSummary {
  code: string;
  name: string;
  level: 'sido' | 'sigungu';
  parentCode: string | null;
  lat: number | null;
  lng: number | null;
  avgPricePerM2: number | null;
  changeRate: number | null;
  dealCount: number;
}

interface DongSummary {
  dong: string;
  lat: number | null;
  lng: number | null;
  avgPricePerM2: number | null;
  changeRate: number | null;
  dealCount: number;
}

interface RecentDeal {
  apt_name: string;
  dong: string;
  area_m2: string;
  floor: number;
  build_year: number | null;
  deal_amount: string;
  deal_ymd: string;
}

type Tier = 'sido' | 'sigungu' | 'sigungu-complex' | 'dong-complex';

type SelectedTarget =
  | { type: 'region'; code: string; name: string; changeRate: number | null; avgPricePerM2: number | null; dealCount: number }
  | { type: 'complex'; regionCode: string; dong: string; aptName: string; latestDealAmount: number | null; latestDealYmd: string | null; dealCount: number };

interface Bounds {
  swLat: number;
  swLng: number;
  neLat: number;
  neLng: number;
}

const CENTER = { lat: 36.5, lng: 127.7 };
const INITIAL_LEVEL = 13;

/** 사용자 표현(줌 6~7 전국 … 12+ 동네)을 카카오 실제 레벨 스케일(작을수록 확대)로 매핑 */
function tierForLevel(level: number): Tier {
  if (level >= 12) return 'sido';
  if (level >= 9) return 'sigungu';
  if (level >= 6) return 'sigungu-complex';
  return 'dong-complex';
}

function roundBounds(b: Bounds | null): Bounds | null {
  if (!b) return null;
  const r = (n: number) => Math.round(n * 1000) / 1000;
  return { swLat: r(b.swLat), swLng: r(b.swLng), neLat: r(b.neLat), neLng: r(b.neLng) };
}

function boundsQuery(b: Bounds): string {
  return `swLat=${b.swLat}&swLng=${b.swLng}&neLat=${b.neLat}&neLng=${b.neLng}`;
}

/** 변동률 → 원 색상 (상승=빨강, 하락=파랑 — 국내 증시/부동산 관례) */
function changeColor(rate: number | null): string {
  if (rate === null) return 'rgba(130,145,170,0.75)';
  if (rate >= 2) return 'rgba(215,50,50,0.8)';
  if (rate >= 0.3) return 'rgba(240,110,90,0.78)';
  if (rate >= -0.3) return 'rgba(130,145,170,0.75)';
  if (rate >= -2) return 'rgba(90,140,225,0.78)';
  return 'rgba(55,95,205,0.8)';
}

function formatPrice(v: number | null): string {
  if (v === null) return '데이터 없음';
  const eok = v / 10000;
  return `${eok.toFixed(1)}억/㎡`;
}

function formatDealAmount(v: string | number): string {
  const n = Number(v);
  if (!Number.isFinite(n)) return '-';
  const eok = Math.floor(n / 10000);
  const rest = n % 10000;
  return rest ? `${eok}억 ${Math.round(rest / 1000)}천만` : `${eok}억`;
}

function HeatCircle({
  name,
  lat,
  lng,
  changeRate,
  onClick,
}: {
  name: string;
  lat: number;
  lng: number;
  changeRate: number | null;
  onClick?: () => void;
}) {
  return (
    <CustomOverlayMap position={{ lat, lng }} yAnchor={0.5}>
      <div
        onClick={onClick}
        className={`select-none rounded-full border-2 border-white/70 px-2.5 py-1.5 text-center shadow-md ${onClick ? 'cursor-pointer' : ''}`}
        style={{ background: changeColor(changeRate) }}
      >
        <div className="text-[11px] font-bold leading-tight text-white drop-shadow">{name}</div>
        <div className="text-[10px] font-extrabold leading-tight text-white drop-shadow">
          {changeRate === null ? '-' : `${changeRate > 0 ? '+' : ''}${changeRate.toFixed(1)}%`}
        </div>
      </div>
    </CustomOverlayMap>
  );
}

export function RealEstateStatusPage() {
  const [, kakaoError] = useKakaoLoader({ appkey: KAKAO_KEY || '', libraries: [] });
  const [mapLevel, setMapLevel] = useState(INITIAL_LEVEL);
  const [mapCenter, setMapCenter] = useState(CENTER);
  const [bounds, setBounds] = useState<Bounds | null>(null);
  const [selectedTarget, setSelectedTarget] = useState<SelectedTarget | null>(null);
  const [lastSigunguRegions, setLastSigunguRegions] = useState<RegionSummary[]>([]);
  const mapRef = useRef<kakao.maps.Map | null>(null);

  const tier = tierForLevel(mapLevel);
  const roundedBounds = useMemo(() => roundBounds(bounds), [bounds]);

  /* Map 은 center/level 을 초기 마운트 값으로만 쓰는 비제어(uncontrolled) 방식으로 둔다(프로그래밍적
     이동은 mapRef 로 직접 setCenter/setLevel). 그리고 값이 실제로 바뀐 경우에만 setState 한다 —
     CustomOverlayMap 자식들이 매 렌더마다 새 position 객체를 받아 내부적으로 재배치되면서 idle 을
     다시 유발할 수 있는데, 매번 새 객체로 setState 하면 그 재렌더가 다시 재배치→idle 을 부르는
     무한 루프(React #185 Maximum update depth exceeded)에 빠진다. */
  const syncMapState = (target: kakao.maps.Map) => {
    const c = target.getCenter();
    const b = target.getBounds();
    const lvl = target.getLevel();
    const nextCenter = { lat: c.getLat(), lng: c.getLng() };
    const nextBounds: Bounds = {
      swLat: b.getSouthWest().getLat(),
      swLng: b.getSouthWest().getLng(),
      neLat: b.getNorthEast().getLat(),
      neLng: b.getNorthEast().getLng(),
    };

    setMapLevel((prev) => (prev === lvl ? prev : lvl));
    setMapCenter((prev) => (prev.lat === nextCenter.lat && prev.lng === nextCenter.lng ? prev : nextCenter));
    setBounds((prev) =>
      prev &&
      prev.swLat === nextBounds.swLat &&
      prev.swLng === nextBounds.swLng &&
      prev.neLat === nextBounds.neLat &&
      prev.neLng === nextBounds.neLng
        ? prev
        : nextBounds,
    );
  };

  const sidoQuery = useQuery({
    queryKey: ['realestate', 'regions', 'sido'],
    queryFn: () => get<{ regions: RegionSummary[] }>('/real-estate/regions?level=sido'),
    staleTime: 10 * 60_000,
  });

  const sigunguEnabled = (tier === 'sigungu' || tier === 'sigungu-complex') && !!roundedBounds;
  const sigunguQuery = useQuery({
    queryKey: ['realestate', 'regions', 'sigungu', roundedBounds],
    queryFn: () =>
      get<{ regions: RegionSummary[] }>(`/real-estate/regions?level=sigungu&${boundsQuery(roundedBounds!)}`),
    enabled: sigunguEnabled,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (sigunguQuery.data) setLastSigunguRegions(sigunguQuery.data.regions);
  }, [sigunguQuery.data]);

  const nearestSigunguCode = useMemo(() => {
    let best: RegionSummary | null = null;
    let bestDist = Infinity;
    for (const r of lastSigunguRegions) {
      if (r.lat === null || r.lng === null) continue;
      const d = (r.lat - mapCenter.lat) ** 2 + (r.lng - mapCenter.lng) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = r;
      }
    }
    return best?.code ?? null;
  }, [lastSigunguRegions, mapCenter]);

  const dongQuery = useQuery({
    queryKey: ['realestate', 'dong-summary', nearestSigunguCode],
    queryFn: () => get<{ dongs: DongSummary[] }>(`/real-estate/dong-summary?parent=${nearestSigunguCode}`),
    enabled: tier === 'dong-complex' && !!nearestSigunguCode,
    staleTime: 5 * 60_000,
  });

  const complexEnabled = (tier === 'sigungu-complex' || tier === 'dong-complex') && !!roundedBounds;
  const complexQuery = useQuery({
    queryKey: ['realestate', 'complexes', roundedBounds],
    queryFn: () =>
      get<{ complexes: ComplexMarker[] }>(`/real-estate/complexes?${boundsQuery(roundedBounds!)}&months=1`),
    enabled: complexEnabled,
    staleTime: 3 * 60_000,
  });

  const dealQuery = useQuery({
    queryKey: ['realestate', 'deals', selectedTarget],
    queryFn: () => {
      if (selectedTarget?.type === 'region') {
        return get<{ trend: { ym: string; avgPrice: number | null; dealCount: number }[]; recentDeals: RecentDeal[] }>(
          `/real-estate/regions/${selectedTarget.code}/deals?months=6`,
        );
      }
      if (selectedTarget?.type === 'complex') {
        return get<{ trend: { ym: string; avgPrice: number | null; dealCount: number }[]; recentDeals: RecentDeal[] }>(
          `/real-estate/regions/${selectedTarget.regionCode}/deals?months=6&aptName=${encodeURIComponent(selectedTarget.aptName)}`,
        );
      }
      return Promise.reject(new Error('no target'));
    },
    enabled: !!selectedTarget,
  });

  const regionsLoading =
    (tier === 'sido' && sidoQuery.isLoading) ||
    ((tier === 'sigungu' || tier === 'sigungu-complex') && sigunguQuery.isLoading) ||
    (tier === 'dong-complex' && dongQuery.isLoading);

  const handleSidoClick = (region: RegionSummary) => {
    if (region.lat === null || region.lng === null || !mapRef.current) return;
    setSelectedTarget(null);
    setMapCenter({ lat: region.lat, lng: region.lng });
    setMapLevel(10);
    mapRef.current.setCenter(new kakao.maps.LatLng(region.lat, region.lng));
    mapRef.current.setLevel(10);
  };

  const handleRegionSelect = (region: RegionSummary) => {
    setSelectedTarget({
      type: 'region',
      code: region.code,
      name: region.name,
      changeRate: region.changeRate,
      avgPricePerM2: region.avgPricePerM2,
      dealCount: region.dealCount,
    });
  };

  const handleComplexClick = (complex: ComplexMarker) => {
    setSelectedTarget({
      type: 'complex',
      regionCode: complex.regionCode,
      dong: complex.dong,
      aptName: complex.aptName,
      latestDealAmount: complex.latestDealAmount,
      latestDealYmd: complex.latestDealYmd,
      dealCount: complex.dealCount,
    });
  };

  const goBack = () => {
    if (!mapRef.current) return;
    setSelectedTarget(null);
    setMapCenter(CENTER);
    setMapLevel(INITIAL_LEVEL);
    mapRef.current.setCenter(new kakao.maps.LatLng(CENTER.lat, CENTER.lng));
    mapRef.current.setLevel(INITIAL_LEVEL);
  };

  const placeholderText =
    tier === 'sido'
      ? '지역을 클릭하면 확대돼요.'
      : tier === 'sigungu'
      ? '지역을 클릭하면 상세 시세를 볼 수 있어요.'
      : '지역 또는 단지 마커를 클릭하면 상세 시세를 볼 수 있어요.';

  return (
    <div className="container-base pt-4 pb-6">
      <SEOHead
        title="부동산 현황"
        description="국토교통부 아파트 매매 실거래가 기반 지역별 시세 지도"
        canonical="/realestate-status"
      />
      <ServiceNav />

      <div className="mb-4 flex items-center gap-2">
        {tier !== 'sido' && (
          <button
            onClick={goBack}
            className="flex items-center gap-1 text-sm font-semibold text-[var(--color-text-sub)] hover:text-[var(--color-text)]"
          >
            <ChevronLeft className="h-4 w-4" /> 전국
          </button>
        )}
        <h1 className="text-2xl font-extrabold">부동산 현황</h1>
      </div>
      <p className="mb-4 text-sm text-[var(--color-text-sub)]">
        국토교통부 아파트 매매 실거래가 기준 · 전월 대비 ㎡당 평균가 변동률
      </p>

      {!KAKAO_KEY || kakaoError ? (
        <div className="flex h-96 items-center justify-center rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text-sub)]">
          지도를 표시할 수 없어요. (지도 설정 준비중)
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_320px]">
          <div className="relative h-[480px] overflow-hidden rounded-2xl border border-[var(--color-border)]">
            {regionsLoading && <Skeleton className="absolute inset-0 z-10 h-full w-full" />}
            <Map
              center={CENTER}
              level={INITIAL_LEVEL}
              style={{ width: '100%', height: '100%' }}
              onCreate={(target) => {
                mapRef.current = target;
                syncMapState(target);
              }}
              onIdle={syncMapState}
            >
              {tier === 'sido' &&
                (sidoQuery.data?.regions ?? []).map(
                  (r) =>
                    r.lat !== null &&
                    r.lng !== null && (
                      <HeatCircle
                        key={r.code}
                        name={r.name}
                        lat={r.lat}
                        lng={r.lng}
                        changeRate={r.changeRate}
                        onClick={() => handleSidoClick(r)}
                      />
                    ),
                )}

              {(tier === 'sigungu' || tier === 'sigungu-complex') &&
                (sigunguQuery.data?.regions ?? []).map(
                  (r) =>
                    r.lat !== null &&
                    r.lng !== null && (
                      <HeatCircle
                        key={r.code}
                        name={r.name}
                        lat={r.lat}
                        lng={r.lng}
                        changeRate={r.changeRate}
                        onClick={() => handleRegionSelect(r)}
                      />
                    ),
                )}

              {tier === 'dong-complex' &&
                (dongQuery.data?.dongs ?? []).map(
                  (d) =>
                    d.lat !== null &&
                    d.lng !== null && (
                      <HeatCircle key={d.dong} name={d.dong} lat={d.lat} lng={d.lng} changeRate={d.changeRate} />
                    ),
                )}

              {(tier === 'sigungu-complex' || tier === 'dong-complex') && complexQuery.data && (
                <ComplexMarkerLayer
                  complexes={complexQuery.data.complexes}
                  clustered={tier === 'sigungu-complex'}
                  onComplexClick={handleComplexClick}
                />
              )}
            </Map>
          </div>

          <div className="space-y-4">
            {!selectedTarget ? (
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-text-sub)]">
                {placeholderText}
              </div>
            ) : (
              <>
                <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                  {selectedTarget.type === 'region' ? (
                    <>
                      <div className="text-lg font-extrabold">{selectedTarget.name}</div>
                      <div
                        className="mt-1 text-2xl font-extrabold"
                        style={{
                          color:
                            selectedTarget.changeRate === null
                              ? undefined
                              : selectedTarget.changeRate > 0
                              ? '#d43030'
                              : selectedTarget.changeRate < 0
                              ? '#3b6fd4'
                              : undefined,
                        }}
                      >
                        {selectedTarget.changeRate === null
                          ? '-'
                          : `${selectedTarget.changeRate > 0 ? '+' : ''}${selectedTarget.changeRate.toFixed(1)}%`}
                      </div>
                      <div className="mt-1 text-xs text-[var(--color-text-sub)]">
                        평균 {formatPrice(selectedTarget.avgPricePerM2)} · 이번달 거래 {selectedTarget.dealCount}건
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-lg font-extrabold">{selectedTarget.aptName}</div>
                      <div className="mt-1 text-2xl font-extrabold">
                        {selectedTarget.latestDealAmount !== null
                          ? formatDealAmount(selectedTarget.latestDealAmount)
                          : '-'}
                      </div>
                      <div className="mt-1 text-xs text-[var(--color-text-sub)]">
                        {selectedTarget.dong} · 최근 거래 {selectedTarget.latestDealYmd ?? '-'} · 최근1개월{' '}
                        {selectedTarget.dealCount}건
                      </div>
                    </>
                  )}
                </div>

                {dealQuery.isLoading ? (
                  <Skeleton className="h-48 w-full" />
                ) : dealQuery.data ? (
                  <>
                    <RealEstateTrendChart data={dealQuery.data.trend} />
                    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
                      <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[var(--color-text-sub)]">
                        최근 거래
                      </div>
                      {dealQuery.data.recentDeals.length === 0 ? (
                        <div className="py-4 text-center text-xs text-[var(--color-text-sub)]">
                          최근 거래 내역이 없어요.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {dealQuery.data.recentDeals.slice(0, 10).map((d, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between border-b border-[var(--color-border)] pb-2 text-xs last:border-0 last:pb-0"
                            >
                              <div>
                                <div className="font-semibold">{d.apt_name}</div>
                                <div className="text-[var(--color-text-sub)]">
                                  {d.dong} · {d.area_m2}㎡ · {d.floor}층 · {d.deal_ymd}
                                </div>
                              </div>
                              <div className="font-bold">{formatDealAmount(d.deal_amount)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
